import { useQuery } from "@tanstack/react-query";
import { api, type ThemeDescriptor, type ThemeId } from "@/lib/api";

export const THEMES_QUERY_KEY = ["themes"] as const;

/**
 * The document-theme descriptors (GET /api/themes).
 *
 * `enabled: false` reads whatever is cached without fetching — a Classic
 * document never needs the list until someone opens Look.
 *
 * They change only with a deploy, so they are fetched once per session and
 * shared by every surface that draws a theme: Document settings → Look, the
 * Export PDF row and the canvas headings.
 */
export function useThemes({ enabled = true }: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: THEMES_QUERY_KEY,
    queryFn: () => api.themes.list(),
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
  return {
    themes: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** One descriptor by id, once the list has loaded. */
export function useTheme(id: ThemeId | null | undefined): ThemeDescriptor | undefined {
  const { themes } = useThemes({ enabled: !!id && id !== "classic" });
  return id ? themes.find((t) => t.id === id) : undefined;
}
